import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Ticket, TrendingUp, BarChart3, DollarSign, Users, ArrowUpRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || window.location.origin}/api`;

const CouponAnalytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const res = await axios.get(`${API}/coupons/analytics`);
        setAnalytics(res.data);
      } catch (err) {
        console.error('Error fetching coupon analytics:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAnalytics();
  }, []);

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 bg-gray-200 rounded w-48"></div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <div key={i} className="h-24 bg-gray-200 rounded-xl"></div>)}
        </div>
      </div>
    );
  }

  if (!analytics) return null;

  const stats = [
    {
      label: 'Total Coupons',
      value: analytics.total_coupons,
      sub: `${analytics.active_coupons} active`,
      icon: Ticket,
      color: '#6366F1',
      bg: '#EEF2FF',
    },
    {
      label: 'Redemptions',
      value: analytics.total_redemptions,
      sub: `${analytics.redemption_rate}% rate`,
      icon: Users,
      color: '#059669',
      bg: '#ECFDF5',
    },
    {
      label: 'Total Savings',
      value: `$${analytics.total_discount_given.toLocaleString('en-US', {minimumFractionDigits: 2})}`,
      sub: `Avg $${analytics.avg_discount.toFixed(2)}/use`,
      icon: DollarSign,
      color: '#D97706',
      bg: '#FFFBEB',
    },
    {
      label: 'Revenue w/ Coupons',
      value: `$${analytics.total_revenue_with_coupons.toLocaleString('en-US', {minimumFractionDigits: 2})}`,
      sub: 'from coupon orders',
      icon: TrendingUp,
      color: '#2563EB',
      bg: '#EFF6FF',
    },
  ];

  return (
    <div data-testid="coupon-analytics">
      {/* Header */}
      <div className="mb-4 flex justify-between items-center">
        <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <BarChart3 size={22} className="text-gray-400" />
          Coupon Analytics
        </h3>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {stats.map((stat) => (
          <div key={stat.label} className="rounded-xl p-4 border border-gray-100 shadow-sm bg-white dark:bg-gray-800 dark:border-gray-700" data-testid={`stat-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: stat.bg }}>
                <stat.icon size={16} style={{ color: stat.color }} />
              </div>
            </div>
            <p className="text-xl font-bold text-gray-900 dark:text-white">{stat.value}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{stat.label}</p>
            <p className="text-xs mt-1" style={{ color: stat.color }}>{stat.sub}</p>
          </div>
        ))}
      </div>

      {/* Top Coupons */}
      {analytics.top_coupons.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
            <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200">Top Performing Coupons</h4>
          </div>
          <div className="divide-y divide-gray-50 dark:divide-gray-700">
            {analytics.top_coupons.map((coupon, idx) => {
              const maxUses = coupon.max_uses || coupon.uses_count || 1;
              const progressPct = Math.min((coupon.uses_count / maxUses) * 100, 100);
              return (
                <div key={idx} className="px-4 py-3 flex items-center gap-4" data-testid={`top-coupon-${coupon.code}`}>
                  <div className="w-8 h-8 rounded-lg bg-indigo-50 dark:bg-indigo-900/30 flex items-center justify-center text-xs font-bold text-indigo-600">
                    #{idx + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-semibold text-sm text-gray-900 dark:text-gray-100">{coupon.code}</span>
                      <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${coupon.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                        {coupon.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="text-xs text-gray-500">
                        {coupon.discount_type === 'percentage' ? `${coupon.discount_value}% off` : `$${coupon.discount_value} off`}
                      </span>
                      <span className="text-xs text-gray-400">|</span>
                      <span className="text-xs text-gray-500">{coupon.uses_count} uses{coupon.max_uses ? ` / ${coupon.max_uses}` : ''}</span>
                    </div>
                    {/* Progress bar */}
                    {coupon.max_uses && (
                      <div className="mt-1.5 h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                        <div className="h-full bg-indigo-500 rounded-full transition-all" style={{ width: `${progressPct}%` }} />
                      </div>
                    )}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-bold text-gray-900 dark:text-gray-100 flex items-center gap-1">
                      ${coupon.total_discount.toFixed(2)}
                      <ArrowUpRight size={12} className="text-green-500" />
                    </p>
                    <p className="text-xs text-gray-500">saved</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Empty state */}
      {analytics.total_redemptions === 0 && (
        <div className="text-center py-8 bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700">
          <Ticket size={36} className="mx-auto mb-2 text-gray-300" />
          <p className="text-sm text-gray-500">No coupon redemptions yet. Share your coupon codes with clients to see analytics here.</p>
        </div>
      )}
    </div>
  );
};

export default CouponAnalytics;
